import {
  RouteLocationRaw,
  NavigationFailure,
  isNavigationFailure,
  RouteLocationNormalized,
  RouteLocation,
  Router,
  useRouter
} from 'vue-router'
import { getSessionStorage, getScrollTop, Env } from '@voghion/fe-util'
import { navigateBack, navigateTo } from '@voghion/native-api'
import { nextTick } from 'vue'
import qs from 'qs'

let routerInited = false

export enum NAVIGATE_TYPE {
  PUSH = 'push',
  REPLACE = 'replace'
}

type NONCYCLIC_VALIDATOR = (route: RouteLocation) => Promise<void>

enum NavigationBarHidden {
  /**
   * 隐藏
   */
  True = 1,
  /**
   * 显示
   */
  False = 0
}

enum StatusBarStyle {
  /**
   * 黑色
   */
  Black = 1,
  /**
   * 白色
   */
  White = 2
}

type WebViewParams = {
  /**
   * 是否隐藏导航栏
   */
  hideNavigationBar?: NavigationBarHidden
  statusBarStyle?: StatusBarStyle
  external?: Number
}

/**
 * 外部路由
 */
type VendorRouteLocationRaw = {
  url: string
}

interface RouterAdv extends Router {
  internalRoute: boolean // 是否是内部导航（由router的api触发）
  latestInternalRoute: boolean
  latestPushRoute: RouteLocationNormalized | null
  latestIsPush: boolean | number
  latestStateKey: string
  historyLength: number
  isPush: number
  isReplace: boolean
  currentInteractivePop: number // 当前的侧滑手势开关
  latestFromRoute: RouteLocationNormalized | null // 上一个route
  latestForwardFromRoute: RouteLocationNormalized | null // 上一个前进的route，这两个参数主要用于判断来源，避免循环跳转
  latestHistoryChangeByGesture: boolean
  isRouting: boolean
  latestWindowScrollTop: number
  latestWindowScrollHeight: number
  /**
   * 全局的 webView 参数
   */
  webViewParams: WebViewParams

  /**
   * 在 app 环境使用新 webView 打开页面
   * @param to
   * @returns
   */
  navigateTo(to: RouteLocationRaw): void

  /**
   * 使用新 webView 打开
   * @param url
   * @param params
   */
  navigateToWebView(url: string, params: WebViewParams): void

  /**
   * 格式化导航路由
   * @param {*} route
   */
  formatNavigationRoute(route: RouteLocationRaw): VendorRouteLocationRaw | RouteLocationRaw

  /**
   * 非循环导航
   * @param to
   * @param validator 自定义校验函数，返回Promise，resolve时继续执行路由，reject时执行back，默认的校验是和上一次的来源路由路径做匹配
   * @param type
   */
  noncyclicNavigate(
    to: RouteLocationRaw,
    validator?: NONCYCLIC_VALIDATOR,
    type?: NAVIGATE_TYPE
  ): Promise<NavigationFailure | void | undefined> | void

  /**
   * 非循环重定向
   * @param to
   */
  noncyclicPush(
    to: RouteLocationRaw,
    validator?: NONCYCLIC_VALIDATOR
  ): Promise<NavigationFailure | void | undefined> | void

  /**
   * 非循环重定向
   * @param to
   */
  noncyclicReplace(
    to: RouteLocationRaw,
    validator?: NONCYCLIC_VALIDATOR
  ): Promise<NavigationFailure | void | undefined> | void
}

const BACK_STATE_CACHE_KEY = 'history_back_states'

/**
 * 获取当前的路由state
 * @returns
 */
function getHistoryState() {
  return window.history.state ? window.history.state.key || '' : ''
}

function initNavigator() {
  if (routerInited) {
    return
  }
  const env = Env.getInstance()
  routerInited = true
  const router = useRouter() as RouterAdv
  router.internalRoute = true // 是否是内部导航（由router的api触发）
  router.latestInternalRoute = true
  router.latestPushRoute = null
  router.latestIsPush = false
  router.latestStateKey = ''
  router.historyLength = window.history.length
  router.isPush = 0
  router.isReplace = false
  router.currentInteractivePop = 1
  router.latestFromRoute = null
  router.latestForwardFromRoute = null
  router.latestHistoryChangeByGesture = false // 上一次路由变化是否由手势触发
  router.isRouting = false
  router.webViewParams = {
    hideNavigationBar: NavigationBarHidden.True,
    statusBarStyle: StatusBarStyle.Black
  }

  router.formatNavigationRoute = (route) => {
    let res: RouteLocationRaw | { url: string } = route
    if (
      typeof route === 'string' &&
      (route.indexOf('//') === 0 ||
        route.indexOf('tel:') === 0 ||
        route.indexOf('sms:') === 0 ||
        route.indexOf('://') > 0)
    ) {
      if (route.indexOf('//') === 0) {
        route = `${location.protocol}${route}`
      }
      res = { url: route }
    }
    return res
  }
  router.navigateToWebView = (url, params: WebViewParams = {}) => {
    params.hideNavigationBar = params.hideNavigationBar ? 1 : 0
    params.statusBarStyle = params.statusBarStyle || 1
    const queryStr = qs.stringify(params)
    navigateTo({
      url: `voghion://approuter/showWeb?openUrl=${encodeURIComponent(url)}&${queryStr}`
    })
  }
  router.navigateTo = (route) => {
    const finalRoute = router.formatNavigationRoute(route)
    const isVendorRoute = typeof finalRoute === 'object' && 'url' in finalRoute
    let url: string
    if (isVendorRoute) {
      url = finalRoute.url
    } else {
      const result = router.resolve(finalRoute)
      url = result.href
      url = `${location.origin}${location.pathname}${location.search}${url}`
    }
    if (env.isApp()) {
      if (url.indexOf('voghion://') === 0) {
        // 如果是 appUrl，直接跳转
        return navigateTo({
          url
        })
      }
      router.navigateToWebView(url, isVendorRoute ? {} : router.webViewParams)
    } else {
      if (!isVendorRoute) {
        router.push(finalRoute)
      } else {
        // 非 app 环境暂时不处理 appLink
      }
    }
    return new Promise((resolve) => {
      setTimeout(resolve, 0)
    })
  }

  /**
   * 重写相关路由方法，提前标识前进后退
   */
  const originalReplace = router.replace
  router.replace = function (to) {
    router.isReplace = true
    return originalReplace.call(this, to)
  }
  const originalPush = router.push
  router.push = function push(to) {
    if (env.isApp()) {
      // app 统一使用新 webView 打开

      return false
    }
    router.internalRoute = true
    router.isPush = 1
    return originalPush.call(this, to)
  }
  const originalGo = router.go
  router.go = function (n) {
    router.internalRoute = true
    if (n > 0) {
      router.isPush = 1
    } else if (n < 0) {
      router.isPush = -1
    }
    originalGo.call(this, n)
  }

  let firstRouteState = -1 // 首个路由状态
  let firstLoad = true // 是否是首次进入页面
  const session = getSessionStorage()
  let backStates = session.getItem(BACK_STATE_CACHE_KEY) || [] // 返回的state记录，用于判断前进后退

  let hasTouchStart = false // 是否触发了touch
  let touchResetTimer: any = 0
  function cancelTouchResetTimer() {
    if (touchResetTimer) {
      clearTimeout(touchResetTimer)
      touchResetTimer = 0
    }
    hasTouchStart = false
  }
  window.addEventListener('touchstart', () => {
    cancelTouchResetTimer()
    hasTouchStart = true
  })
  window.addEventListener('touchend', () => {
    touchResetTimer = setTimeout(() => {
      hasTouchStart = false
    }, 100)
  })
  /**
   * 判断路由是前进还是后退，主要用于确认后续的转场动画，即使后续的钩子取消也不影响，所以提前到 beforeEach
   */
  router.beforeEach((_to, from, next) => {
    if (firstLoad) {
      // 首次进入都当做push
      router.isPush = 1
    }
    if (router.isPush === 0) {
      // 浏览器触发的前进后退，此时 stateKey 已经变化
      const stateKey = getHistoryState()
      const forwardIndex = backStates.indexOf(stateKey)
      router.isPush = forwardIndex < 0 ? -1 : 1
    }
    router.latestIsPush = router.isPush > 0
    // 记录首次的来源路由
    if (!firstLoad || !router.latestFromRoute) {
      router.latestFromRoute = from
      if (router.latestIsPush) {
        router.latestForwardFromRoute = from
      } else {
        router.latestForwardFromRoute = null
      }
    }
    // 在 store 记录状态
    router.latestInternalRoute = router.internalRoute
    const isGesture = hasTouchStart && !router.internalRoute
    router.latestHistoryChangeByGesture = isGesture
    router.internalRoute = false
    cancelTouchResetTimer()
    next()
  })
  router.latestWindowScrollTop = 0 // window 滚动位置
  router.latestWindowScrollHeight = 0 // window 内容高度
  const beforeResolve = router.beforeResolve || router.beforeEach

  /**
   * 路由确认后的处理
   */
  beforeResolve.bind(router)((_to, _from, next) => {
    router.isRouting = true
    // 记录当前页面滚动位置
    router.latestWindowScrollTop = getScrollTop()
    router.latestWindowScrollHeight = Math.max(document.documentElement.scrollHeight || 0, document.body.scrollHeight)
    next()
  })

  /**
   * 路由导航后的处理
   */
  router.afterEach((to, _from, failure) => {
    if (!isNavigationFailure(failure)) {
      if (typeof router.latestIsPush === 'number' && router.latestIsPush > 0) {
        router.latestPushRoute = to
      }
      nextTick(() => {
        router.historyLength = window.history.length
        // 重新获取一次最新的state，如果是router内部api触发的导航变化，只能在下一个循环里获取到变化的state
        const stateKey = getHistoryState()
        if (typeof router.latestIsPush === 'number' && router.latestIsPush > 0) {
          if (!firstLoad) {
            const forwardIndex = backStates.indexOf(stateKey)
            backStates = backStates.slice(0, Math.max(forwardIndex, 0))
          }
        } else {
          backStates.push(router.latestStateKey)
        }
        const session = getSessionStorage()
        session.setItem(BACK_STATE_CACHE_KEY, backStates)
        if (firstRouteState === -1 || window.history.length === 1) {
          firstRouteState = stateKey
        }
        router.latestStateKey = stateKey

        // 重置相关标识
        router.isPush = 0
        router.isReplace = false
        router.isRouting = false
        firstLoad = false
      })
    } else {
      router.isPush = 0
      router.isReplace = false
      router.isRouting = false
      firstLoad = false
    }
  })

  const originBack = router.back
  router.back = function () {
    const href = window.location.href
    originBack()
    setTimeout(() => {
      if (href === window.location.href && env.isApp()) {
        navigateBack()
      }
    }, 50)
  }

  router.noncyclicNavigate = function (location, validator, type = NAVIGATE_TYPE.PUSH) {
    const navigateFunc = router[type] || router.push
    const route = router.resolve(location)
    if (!route) {
      return navigateFunc.call(router, location)
    }
    if (validator) {
      return validator(route).then(
        () => {
          return navigateFunc.call(router, location)
        },
        () => {
          return router.back()
        }
      )
    } else {
      const back = window.history.state.back || ''
      if (back && route.fullPath === back) {
        return router.back()
      } else {
        return navigateFunc.call(router, location)
      }
    }
  }

  router.noncyclicPush = function (location, validator) {
    return router.noncyclicNavigate(location, validator, NAVIGATE_TYPE.PUSH)
  }

  router.noncyclicReplace = function (location, validator) {
    return router.noncyclicNavigate(location, validator, NAVIGATE_TYPE.REPLACE)
  }
}

/**
 * 扩展 router，支持更复杂的导航场景
 * @returns
 */
export const useRouterAdv = function () {
  const router = useRouter()
  initNavigator()
  return router as RouterAdv
}
